<template>
  <VSearchable
    class="v-searchable-table"
    :search="search"
    :placeholder="placeholder"
    :disabled="disabled"
    :loading="loading"
    :count="count"
    :auto-load="autoLoad"
    :page-count="options.length"
    @update:search="$emit('update:search', $event)"
    @load-more="$emit('load-more', $event)"
  >
    <template #no-results><slot name="no-results" /></template>
    <table class="w-full mb-0 border border-gray-300" :class="{ 'table-fixed': layout === 'fixed' }">
      <thead v-if="options.length">
        <tr>
          <slot name="headers" />
        </tr>
      </thead>
      <tbody>
        <tr
          v-for="option in options"
          :key="option.id"
          class="option cursor-pointer"
          :class="{ selected: isCurrent(option) }"
          @click.stop.prevent="onSelected(option)"
        >
          <slot name="option" :item="option" :selected="currentItem && option.id === currentItem.id" />
        </tr>
      </tbody>
      <tfoot>
        <tr v-if="loading && autoLoad && options.length !== 0">
          <td class="relative left-1/2 transform -translate-x-1/2">
            <FontAwesomeIcon class="text-gray-300" icon="circle-notch" spin size="2x" />
          </td>
        </tr>
      </tfoot>
    </table>
  </VSearchable>
</template>

<script lang="ts" setup>
import type { PropType } from 'vue'

export interface Option {
  id: string | number
}

const props = defineProps({
  options: {
    type: Array as PropType<Option[]>,
    required: true,
  },
  currentItem: {
    type: Object as PropType<Option | null>,
    default: null,
  },
  count: {
    type: Number,
    default: null,
  },
  placeholder: {
    type: String,
    default: 'Search\u2026',
  },
  search: {
    type: String as PropType<string | null>,
    default: '',
  },
  loading: {
    type: Boolean,
    default: false,
  },
  autoLoad: {
    type: Boolean,
    default: false,
  },
  disabled: {
    type: Boolean,
    default: false,
  },
  multiple: {
    type: Boolean,
    default: false,
  },
  layout: {
    type: String,
    default: null,
  },
})

const emit = defineEmits(['selected', 'update:search', 'load-more'])

function onSelected(option: Option) {
  emit('selected', option)
}

function isCurrent(option: Option, item = props.currentItem): boolean {
  if (!item) return false
  if (props.multiple && Array.isArray(item)) {
    return item.some(singleItem => isCurrent(option, singleItem))
  }
  return option.id === item.id
}
</script>

<style lang="stylus" scoped>
.v-searchable-table
  thead
    @apply border-b border-gray-300
  thead :deep(td),
  thead :deep(th)
    @apply sticky -top-px

  tbody tr:hover :deep(td)
    text-black bg-gray-450

  tbody tr.selected
    outline 2px solid theme('colors.copper.700')
    outline-offset -1px
    :deep(td)
      @apply bg-copper-100 text-copper-800
</style>
